import { GameState } from '../core/GameState';
import { eventBus } from '../core/EventBus';
import { choose } from '../utils/random';

interface Shimmer {
  id: number;
  type: 'golden' | 'reindeer';
  x: number;
  y: number;
  life: number;
  maxLife: number;
  el: HTMLElement | null;
}

export class ShimmerSystem {
  private shimmers: Shimmer[] = [];
  private nextId = 0;
  private timeSinceLastGolden = 0;
  private goldenInterval = 0;
  private container: HTMLElement | null = null;

  init(): void {
    this.container = document.getElementById('shimmers');
    this.resetGoldenTimer();
  }

  update(state: GameState, dtMs: number): void {
    this.timeSinceLastGolden += dtMs / 1000;

    if (this.timeSinceLastGolden >= this.goldenInterval && this.shimmers.length < 2) {
      this.spawnGolden(state);
      this.resetGoldenTimer();
    }

    for (let i = this.shimmers.length - 1; i >= 0; i--) {
      const s = this.shimmers[i];
      s.life -= dtMs / 1000;
      if (s.life <= 0) {
        this.removeShimmer(i);
      }
    }
  }

  private spawnGolden(_state: GameState): void {
    if (!this.container) return;

    const el = document.createElement('div');
    el.className = 'shimmer golden';
    el.textContent = '🌟';
    el.style.left = `${10 + Math.random() * 70}%`;
    el.style.top = `${10 + Math.random() * 70}%`;

    const shimmer: Shimmer = {
      id: this.nextId++,
      type: 'golden',
      x: 0,
      y: 0,
      life: 13,
      maxLife: 13,
      el,
    };

    el.addEventListener('click', () => this.clickShimmer(shimmer));
    this.container.appendChild(el);
    this.shimmers.push(shimmer);

    eventBus.emit('shimmer:spawned', shimmer.type);
  }

  private clickShimmer(shimmer: Shimmer): void {
    const idx = this.shimmers.indexOf(shimmer);
    if (idx === -1) return;

    eventBus.emit('shimmer:clicked', shimmer.type);
    this.applyGoldenEffect(shimmer);
    this.removeShimmer(idx);
  }

  private applyGoldenEffect(shimmer: Shimmer): void {
    if (shimmer.type !== 'golden') return;

    const effects: Array<() => void> = [
      () => eventBus.emit('golden:frenzy'),
      () => eventBus.emit('golden:click_frenzy'),
      () => eventBus.emit('golden:lucky'),
    ];

    choose(effects)();
  }

  private removeShimmer(index: number): void {
    const shimmer = this.shimmers[index];
    shimmer.el?.remove();
    this.shimmers.splice(index, 1);
  }

  private resetGoldenTimer(): void {
    this.timeSinceLastGolden = 0;
    this.goldenInterval = 60 + Math.random() * 300;
  }

  getActiveCount(): number {
    return this.shimmers.length;
  }

  debugSpawnGolden(state: GameState): void {
    this.spawnGolden(state);
    this.resetGoldenTimer();
  }

  debugTriggerGolden(effect: 'random' | 'frenzy' | 'click_frenzy' | 'lucky' | 'clot' = 'random'): void {
    if (effect === 'random') {
      this.applyGoldenEffect({
        id: -1,
        type: 'golden',
        x: 0,
        y: 0,
        life: 0,
        maxLife: 0,
        el: null,
      });
      return;
    }

    if (effect === 'frenzy') eventBus.emit('golden:frenzy');
    if (effect === 'click_frenzy') eventBus.emit('golden:click_frenzy');
    if (effect === 'lucky') eventBus.emit('golden:lucky');
    if (effect === 'clot') eventBus.emit('golden:clot');
  }
}
